import React from "react";
import { Company, ContractOverviewResponse } from "../../types/addServiceTypes";
import { useNavigate } from "react-router-dom";

interface ContractSuccessModalProps {
  showModal: boolean;
  onClose: () => void;
  contractOverview: ContractOverviewResponse | null;
  selectedUserCompany: Company | null;
  onViewContracts: () => void;
  onAddAnother: () => void;
  requiresSecondContract?: boolean;
  pendingDirection?: "injection" | "offtake";
  onCreateSecondContract?: () => void;
  bothContractsComplete?: boolean;
  justSignedDirection?: "injection" | "offtake" | "combined"; // Add this prop
}

export const ContractSuccessModal: React.FC<ContractSuccessModalProps> = ({
  showModal,
  onClose,
  contractOverview,
  selectedUserCompany,
  onViewContracts,
  onAddAnother,
  requiresSecondContract = false,
  pendingDirection,
  onCreateSecondContract,
  bothContractsComplete = false,
  justSignedDirection,
}) => {
  const navigate = useNavigate();

  if (!showModal) return null;

  // Determine what was just signed more accurately
  const getJustSignedType = () => {
    if (justSignedDirection === "combined") return "Combined (Injection & Offtake)";
    if (justSignedDirection === "injection") return "Injection";
    if (justSignedDirection === "offtake") return "Offtake";

    // Fallback to contract overview
    if (contractOverview?.contract_type === "combined") return "Combined (Injection & Offtake)";
    if (contractOverview?.contract_type === "injection") return "Injection";
    if (contractOverview?.contract_type === "offtake") return "Offtake";

    return "Contract";
  };

  const clearContractLocalStorage = () => {
    // Clear specific contract-related items
    localStorage.removeItem("selectedServices");
    localStorage.removeItem("addServiceCompanyId");
    localStorage.removeItem("contractOverview");
    localStorage.removeItem("selectedCompanyId");
    localStorage.removeItem("formVolumes");
    localStorage.removeItem("lastContractPayload");
    localStorage.removeItem("pslData");
    localStorage.removeItem("pendingDirection");
    localStorage.removeItem("addServiceFormData");
    localStorage.removeItem("injectionFormData");
    localStorage.removeItem("pslFullData");
    localStorage.removeItem("offtakeFormData");
  };

  const justSignedType = getJustSignedType();
  const pendingType = pendingDirection === "injection" ? "Injection" : pendingDirection === "offtake" ? "Offtake" : "";

  // Check if we just signed injection and need offtake (or vice versa)
  const needsSecondContract = requiresSecondContract && pendingDirection && !bothContractsComplete;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={() => {
            clearContractLocalStorage();
            onClose();
            navigate("/overview");
          }}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-2xl transform transition-all max-w-lg w-full">
          {/* Close button */}
          <button
            onClick={() => {
              clearContractLocalStorage();
              onClose();
              navigate("/overview");
            }}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Content */}
          <div className="p-8">
            {/* Success Animation Icon */}
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 mb-6 shadow-lg">
              <svg className="h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            {/* Title & Description */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                {bothContractsComplete
                  ? "All Contracts Successfully Signed!"
                  : needsSecondContract
                  ? `${justSignedType} Contract Successfully Signed!`
                  : "Contract Successfully Signed!"}
              </h3>
              <p className="text-primary-500 text-base leading-relaxed">
                {bothContractsComplete
                  ? "Excellent! Both your injection and offtake contracts have been signed successfully. You will receive confirmation emails shortly."
                  : needsSecondContract
                  ? `Great! Your ${justSignedType.toLowerCase()} contract has been signed. Now you need to create the ${pendingType.toLowerCase()} contract to complete your setup.`
                  : justSignedDirection === "combined"
                  ? "Your injection and offtake contracts have been signed successfully. You will receive confirmation emails shortly."
                  : "Your contract has been signed successfully. You will receive a confirmation email shortly."}
              </p>
            </div>

            {/* Contract Summary Card */}
            <div className="bg-gradient-to-br rounded-xl p-5 mb-8 border border-gray-200">
              <h4 className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-4">Contract Summary</h4>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span className="text-sm text-primary-600">Type</span>
                  </div>
                  <span className="text-sm font-semibold text-primary-600">{justSignedType}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span className="text-sm text-primary-600">EAN</span>
                  </div>
                  <span className="text-sm font-mono font-semibold text-primary-600">{contractOverview?.ean}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span className="text-sm text-primary-600">Company</span>
                  </div>
                  <span className="text-sm font-semibold text-primary-600 truncate max-w-[200px]">
                    {selectedUserCompany?.companyName}
                  </span>
                </div>
              </div>
            </div>

            {/* Progress Indicator for Second Contract - Show when injection is done and offtake is pending */}
            {needsSecondContract && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                      {justSignedDirection === "injection" ? "Injection" : "Offtake"}
                    </span>
                  </div>
                  <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 w-1/2"></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-xs">2</span>
                    </div>
                    <span className="text-sm font-medium text-gray-500">{pendingType}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">Complete both contracts to activate your service</p>
              </div>
            )}

            {/* Both Contracts Complete Indicator */}
            {bothContractsComplete && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-green-800">Both contracts completed successfully!</p>
                    <p className="text-xs text-green-600 mt-1">Injection ✓ • Offtake ✓</p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {needsSecondContract ? (
                <>
                  {/* Primary Action - Create Second Contract */}
                  <button
                    onClick={onCreateSecondContract}
                    className="w-full bg-primary-600 text-white font-semibold py-3 px-6 rounded-xl hover:bg-primary-700 transform hover:scale-[1.02] transition-all shadow-lg hover:shadow-xl"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Please Proceed to Create {pendingType} Contract
                    </div>
                  </button>

                  {/* Secondary Action - Skip */}
                  <button
                    onClick={onViewContracts}
                    className="w-full bg-white border-2 border-gray-300 text-gray-700 font-semibold py-2.5 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-400 transform hover:scale-[1.02] transition-all"
                  >
                    Skip for now
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      clearContractLocalStorage();
                      navigate("/documents");
                    }}
                    className="w-full bg-primary-600 text-white font-semibold py-3 px-6 rounded-xl hover:bg-primary-700 transform hover:scale-[1.02] transition-all shadow-lg hover:shadow-xl"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      View All Contracts
                    </div>
                  </button>

                  {/* Secondary Actions */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={onAddAnother}
                      className="bg-white border-2 border-primary-600 text-primary-600 font-semibold py-2.5 px-4 rounded-xl hover:bg-primary-50 transform hover:scale-[1.02] transition-all"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Another
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        clearContractLocalStorage();
                        navigate("/overview");
                      }}
                      className="bg-white border-2 border-gray-300 text-gray-700 font-semibold py-2.5 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-400 transform hover:scale-[1.02] transition-all"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                          />
                        </svg>
                        Overview
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Optional: Quick tip */}
            <div className="mt-6 p-3 border border-primary-200 rounded-lg">
              <div className="flex gap-2">
                <p className="text-xs text-primary-700">
                  {bothContractsComplete
                    ? "Both contracts have been successfully signed and are now active."
                    : needsSecondContract
                    ? `You've completed the ${justSignedDirection} contract. The ${pendingType.toLowerCase()} contract is required to complete your setup.`
                    : "You'll receive an email confirmation with the contract details shortly."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
